import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, ImagePlus, Loader2, Star, X } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export const Stars = ({ value = 0, size = "h-4 w-4", onPick }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        onClick={onPick ? () => onPick(i) : undefined}
        className={`${size} ${onPick ? "cursor-pointer" : ""} ${
          i <= Math.round(value) ? "fill-gold text-gold" : "text-slate-300"
        }`}
        data-testid={onPick ? `review-star-${i}` : undefined}
      />
    ))}
  </span>
);

export const ProductReviews = ({ productId }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ rating: 5, comment: "", images: [] });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.url].slice(0, 3) }));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const load = async () => {
    const { data: d } = await api.get(`/products/${productId}/reviews`);
    setData(d);
    if (user) {
      try {
        const { data: s } = await api.get(`/products/${productId}/reviews/can-review`);
        setStatus(s);
      } catch {
        setStatus(null);
      }
    }
  };

  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, user?.user_id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/products/${productId}/reviews`, form);
      toast.success("Merci pour votre avis !");
      setForm({ rating: 5, comment: "", images: [] });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;

  return (
    <section className="mt-10" data-testid="product-reviews">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-xl font-bold text-slate-900">Avis clients</h2>
        {data.count > 0 ? (
          <span className="flex items-center gap-2 text-sm text-slate-600" data-testid="reviews-summary">
            <Stars value={data.average} />
            <span className="font-num font-bold text-slate-900">{data.average}/5</span>
            <span>({data.count} avis)</span>
          </span>
        ) : (
          <span className="text-sm text-slate-500">Aucun avis pour le moment</span>
        )}
      </div>

      {status?.can_review && (
        <form onSubmit={submit} className="ds-card mt-4 space-y-3 p-4" data-testid="review-form">
          <div>
            <label className="ds-label">Votre note *</label>
            <div className="mt-1">
              <Stars value={form.rating} size="h-7 w-7" onPick={(r) => setForm({ ...form, rating: r })} />
            </div>
          </div>
          <div>
            <label className="ds-label">Votre commentaire</label>
            <textarea
              rows={3}
              maxLength={600}
              className="ds-input mt-1"
              placeholder="Qualité, emballage, délai de livraison…"
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              data-testid="review-comment"
            />
          </div>
          <div>
            <label className="ds-label">Photos du produit reçu (3 maximum, optionnel)</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {form.images.map((img, i) => (
                <div key={i} className="relative h-20 w-20 overflow-hidden rounded-xl border border-slate-200">
                  <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })}
                    className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-rose-600 text-white"
                    data-testid={`review-remove-image-${i}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {form.images.length < 3 && (
                <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-xl border border-dashed border-slate-300 text-slate-400 hover:border-brand">
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                    data-testid="review-image-input"
                  />
                </label>
              )}
            </div>
          </div>
          <button type="submit" disabled={busy} className="ds-btn-primary w-full sm:w-auto" data-testid="review-submit">
            Publier mon avis
          </button>
        </form>
      )}
      {status && !status.can_review && (
        <p className="mt-3 text-xs text-slate-500" data-testid="review-eligibility">
          {status.already_reviewed
            ? "Vous avez déjà donné votre avis sur ce produit. Merci !"
            : "Seuls les clients ayant reçu ce produit peuvent laisser un avis."}
        </p>
      )}
      {!user && (
        <p className="mt-3 text-xs text-slate-500" data-testid="review-login-hint">
          Connectez-vous pour laisser un avis après réception de votre commande.
        </p>
      )}

      <div className="mt-4 space-y-3">
        {data.items.map((r) => (
          <div key={r.id} className="ds-card p-4" data-testid={`review-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                {r.author}
                {r.verified_purchase && (
                  <span className="flex items-center gap-1 text-[11px] font-medium text-brand">
                    <BadgeCheck className="h-3.5 w-3.5" /> achat vérifié
                  </span>
                )}
              </span>
              <span className="flex items-center gap-2 text-xs text-slate-500">
                <Stars value={r.rating} size="h-3.5 w-3.5" /> {fmtDate(r.created_at)}
              </span>
            </div>
            {r.comment && <p className="mt-2 text-sm text-slate-600">{r.comment}</p>}
            {(r.images || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {r.images.map((img, i) => (
                  <a key={i} href={mediaUrl(img)} target="_blank" rel="noopener noreferrer" data-testid={`review-photo-${r.id}-${i}`}>
                    <img src={mediaUrl(img)} alt="" className="h-20 w-20 rounded-xl border border-slate-200 object-cover transition-transform hover:scale-105" />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
